// src/modulos/Parametros/CFOPRegisterPage.js
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from '../Registers/Registers.module.css';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import {
    PageHeader,
    SearchBar,
    Pagination,
    TableSkeleton,
    EmptyState,
    FormModal,
    Toast
} from '../../components/common';

const CFOP_COLLECTION = 'parameters/data/cfop';
const ITEMS_PER_PAGE = 10;

// ==================== DROPDOWN OPTIONS ====================

// Block 2 - ICMS/ICMSST/IPI
const DEBITO_CREDITO_OPTIONS = ['Debita', 'Credita', 'Não há'];

// Situação Tributária IPI Entrada
const IPI_ENTRADA_OPTIONS = [
    { code: '00', label: '00 - Entrada com recuperação de crédito' },
    { code: '01', label: '01 - Entrada tributada com alíquota zero' },
    { code: '02', label: '02 - Entrada isenta' },
    { code: '03', label: '03 - Entrada não-tributada' },
    { code: '04', label: '04 - Entrada imune' },
    { code: '05', label: '05 - Entrada com suspensão' },
    { code: '49', label: '49 - Outras entradas' },
];

// Situação Tributária IPI Saída
const IPI_SAIDA_OPTIONS = [
    { code: '50', label: '50 - Saída tributada' },
    { code: '51', label: '51 - Saída tributada com alíquota zero' },
    { code: '52', label: '52 - Saída isenta' },
    { code: '53', label: '53 - Saída não-tributada' },
    { code: '54', label: '54 - Saída imune' },
    { code: '55', label: '55 - Saída com suspensão' },
    { code: '99', label: '99 - Outras saídas' },
];

// Block 3 - COFINS
const COFINS_OPTIONS = [
    { code: '01', label: '01 – Operação Tributável com Alíquota Básica' },
    { code: '02', label: '02 - Operação Tributável com Alíquota Diferenciada' },
    { code: '03', label: '03 - Operação Tributável com Alíquota por Unidade de Medida de Produto' },
    { code: '04', label: '04 - Operação Tributável Monofásica - Revenda a Alíquota Zero' },
    { code: '05', label: '05 - Operação Tributável por Substituição Tributária' },
    { code: '06', label: '06 - Operação Tributável a Alíquota Zero' },
    { code: '07', label: '07 - Operação Isenta da Contribuição' },
    { code: '08', label: '08 - Operação sem Incidência da Contribuição' },
    { code: '09', label: '09 - Operação com Suspensão da Contribuição' },
    { code: '49', label: '49 - Outras Operações de Saída' },
    { code: '50', label: '50 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita Tributada no Mercado Interno' },
    { code: '51', label: '51 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receitas Não-Tributadas no Mercado Interno' },
    { code: '52', label: '52 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita de Exportação' },
    { code: '53', label: '53 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno' },
    { code: '54', label: '54 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas no Mercado Interno e de Exportação' },
    { code: '55', label: '55 - Operação com Direito a Crédito - Vinculada a Receitas Não Tributadas no Mercado Interno e de Exportação' },
    { code: '56', label: '56 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno e de Exportação' },
    { code: '60', label: '60 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Tributada no Mercado Interno' },
    { code: '61', label: '61 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Não-Tributada no Mercado Interno' },
    { code: '62', label: '62 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita de Exportação' },
    { code: '63', label: '63 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno' },
    { code: '64', label: '64 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas no Mercado Interno e de Exportação' },
    { code: '65', label: '65 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação' },
    { code: '66', label: '66 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno e de Exportação' },
    { code: '67', label: '67 - Crédito Presumido - Outras Operações' },
    { code: '70', label: '70 - Operação de Aquisição sem Direito a Crédito' },
    { code: '71', label: '71 - Operação de Aquisição com Isenção' },
    { code: '72', label: '72 - Operação de Aquisição com Suspensão' },
    { code: '73', label: '73 - Operação de Aquisição a Alíquota Zero' },
    { code: '74', label: '74 - Operação de Aquisição sem Incidência da Contribuição' },
    { code: '75', label: '75 - Operação de Aquisição por Substituição Tributária' },
    { code: '98', label: '98 - Outras Operações de Entrada' },
    { code: '99', label: '99 - Outras Operações' },
];

// Block 3 - PIS
const PIS_OPTIONS = [
    { code: '01', label: '01 – Operação Tributável com Alíquota Básica' },
    { code: '02', label: '02 - Operação Tributável com Alíquota Diferenciada' },
    { code: '03', label: '03 - Operação Tributável com Alíquota por Unidade de Medida de Produto' },
    { code: '04', label: '04 - Operação Tributável Monofásica - Revenda a Alíquota Zero' },
    { code: '05', label: '05 - Operação Tributável por Substituição Tributária' },
    { code: '06', label: '06 - Operação Tributável a Alíquota Zero' },
    { code: '07', label: '07 - Operação Isenta da Contribuição' },
    { code: '08', label: '08 - Operação sem Incidência da Contribuição' },
    { code: '09', label: '09 - Operação com Suspensão da Contribuição' },
    { code: '49', label: '49 - Outras Operações de Saída' },
    { code: '50', label: '50 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita Tributada no Mercado Interno' },
    { code: '51', label: '51 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receitas Não-Tributadas no Mercado Interno' },
    { code: '52', label: '52 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita de Exportação' },
    { code: '53', label: '53 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno' },
    { code: '54', label: '54 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas no Mercado Interno e de Exportação' },
    { code: '55', label: '55 - Operação com Direito a Crédito - Vinculada a Receitas Não Tributadas no Mercado Interno e de Exportação' },
    { code: '56', label: '56 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno e de Exportação' },
    { code: '60', label: '60 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Tributada no Mercado Interno' },
    { code: '61', label: '61 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Não-Tributada no Mercado Interno' },
    { code: '62', label: '62 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita de Exportação' },
    { code: '63', label: '63 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno' },
    { code: '64', label: '64 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas no Mercado Interno e de Exportação' },
    { code: '65', label: '65 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação' },
    { code: '66', label: '66 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno e de Exportação' },
    { code: '67', label: '67 - Crédito Presumido - Outras Operações' },
    { code: '70', label: '70 - Operação de Aquisição sem Direito a Crédito' },
    { code: '71', label: '71 - Operação de Aquisição com Isenção' },
    { code: '72', label: '72 - Operação de Aquisição com Suspensão' },
    { code: '73', label: '73 - Operação de Aquisição a Alíquota Zero' },
    { code: '74', label: '74 - Operação de Aquisição sem Incidência da Contribuição' },
    { code: '75', label: '75 - Operação de Aquisição por Substituição Tributária' },
    { code: '98', label: '98 - Outras Operações de Entrada' },
    { code: '99', label: '99 - Outras Operações' },
];

// Block 4 - Métodos de cálculo do custo médio
const METODO_CUSTO_MEDIO_OPTIONS = [
    'Utiliza custo médio',
    'Recalcula custo médio',
    'Total da NF menos impostos (ICMS, IPI)',
    'Custo médio da operação de saída',
    'Não controla custo médio'
];

const MOVIMENTACAO_OPTIONS = ['Credita', 'Debita', 'Não há'];
const SIM_NAO_OPTIONS = ['Sim', 'Não'];
const MOVIMENTACAO_NF_OPTIONS = ['Saída', 'Entrada'];

// Block 5 - Substituição tributária
const SUBST_TRIBUTARIA_OPTIONS = ['Incide', 'Não há'];

// Situação tributária ICMS padrão
const ICMS_ST_OPTIONS = [
    { code: '00', label: '00 - Tributada Integralmente' },
    { code: '10', label: '10 - Tributada e com Cobrança do ICMS por Substituição Tributária' },
    { code: '101', label: '101 - Tributada pelo Simples Nacional com permissão de crédito' },
    { code: '102', label: '102 - Tributada pelo Simples Nacional sem permissão de crédito' },
    { code: '103', label: '103 - Isenção do ICMS no Simples Nacional para faixa de receita bruta' },
    { code: '20', label: '20 - Com Redução de Base de Cálculo' },
    { code: '201', label: '201 - Tributada pelo Simples Nacional com perm. de créd. e com cob. do ICMS por ST' },
    { code: '202', label: '202 - Tributada pelo Simples Nacional sem perm. de créd. e com cob. do ICMS por ST' },
    { code: '203', label: '203 - Isenção do ICMS no Simples Nacional p/ faixa de receita bruta e com cob. do ICMS por ST' },
    { code: '30', label: '30 - Isenta ou não Tributada e com Cobrança do ICMS por Substituição Tributária' },
    { code: '300', label: '300 - Imune' },
    { code: '40', label: '40 - Isenta' },
    { code: '400', label: '400 - Não tributada pelo Simples Nacional' },
    { code: '41', label: '41 - Não Tributada' },
    { code: '50', label: '50 - Suspensão' },
    { code: '500', label: '500 - ICMS cobrado anteriormente por substituição tributária (substituído) ou por antecipação' },
    { code: '51', label: '51 - Diferimento' },
    { code: '60', label: '60 - ICMS cobrado anteriormente por Substituição Tributária' },
    { code: '70', label: '70 - Com redução de Base de Cálculo e Cobrança do ICMS por Substituição Tributária' },
    { code: '90', label: '90 - Outras' },
    { code: '900', label: '900 - Outros' },
];

// Classificação tributária IS
const CLASSIF_TRIBUTARIA_IS_OPTIONS = [
    { code: '000', label: '000 - Tributação integral' },
    { code: '010', label: '010 - Tributação com alíquotas uniformes' },
    { code: '011', label: '011 - Tributação com alíquotas uniformes reduzidas' },
    { code: '200', label: '200 - Alíquota reduzida' },
    { code: '220', label: '220 - Alíquota fixa' },
    { code: '221', label: '221 - Alíquota fixa proporcional' },
    { code: '222', label: '222 - Redução de Base de Cálculo' },
    { code: '400', label: '400 - Isenção' },
    { code: '410', label: '410 - Imunidade e não incidência' },
    { code: '510', label: '510 - Diferimento' },
    { code: '515', label: '515 - Diferimento com redução de alíquota' },
    { code: '550', label: '550 - Suspensão' },
    { code: '620', label: '620 - Tributação Monofásica' },
    { code: '800', label: '800 - Transferência de crédito' },
    { code: '810', label: '810 - Ajuste de IBS na ZFM' },
    { code: '811', label: '811 - Ajustes' },
    { code: '820', label: '820 - Tributação em declaração de regime específico' },
    { code: '830', label: '830 - Exclusão da Base de Cálculo' },
];

const CLASSIF_TRIBUTARIA_IBS_CBS_OPTIONS = CLASSIF_TRIBUTARIA_IS_OPTIONS;

// Helper para renderizar selects com opção "-- Selecione --"
const renderSelectWithPlaceholder = (options, value, onChange, placeholder = "-- Selecione --", getValue = (opt) => opt.code || opt, getLabel = (opt) => opt.label || opt) => {
    return (
        <select className={styles.select} value={value || ""} onChange={onChange}>
            <option value="">{placeholder}</option>
            {options.map(opt => {
                const val = getValue(opt);
                const label = getLabel(opt);
                return <option key={val} value={val}>{label}</option>;
            })}
        </select>
    );
};

// ==================== COMPONENTE PRINCIPAL ====================

const CFOPRegisterPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('cadastro_de_cfop');
    const { data: cfopList, loading, addDocument, updateDocument, deleteDocument } = useFirestore(CFOP_COLLECTION);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [formLoading, setFormLoading] = useState(false);
    const [toasts, setToasts] = useState([]);

    // Estado do formulário
    const [codigoOperacao, setCodigoOperacao] = useState('');
    const [descricao, setDescricao] = useState('');
    const [icmsIpiMovimentacao, setIcmsIpiMovimentacao] = useState('');
    const [situacaoTributariaIpiEntrada, setSituacaoTributariaIpiEntrada] = useState('');
    const [situacaoTributariaIpiSaida, setSituacaoTributariaIpiSaida] = useState('');
    const [codigoEnquadramento, setCodigoEnquadramento] = useState('');
    const [situacaoTributariaCofins, setSituacaoTributariaCofins] = useState('');
    const [situacaoTributariaPis, setSituacaoTributariaPis] = useState('');
    const [metodoCustoMedio, setMetodoCustoMedio] = useState('');
    const [movimentacaoEstoque, setMovimentacaoEstoque] = useState('');
    const [estoqueTerceirosGera, setEstoqueTerceirosGera] = useState(false);
    const [comissaoMovimentacao, setComissaoMovimentacao] = useState('');
    const [movimentacaoNf, setMovimentacaoNf] = useState('');
    const [contabContasReceber, setContabContasReceber] = useState('');
    const [contabReceberMercadoria, setContabReceberMercadoria] = useState(false);
    const [contabReceberServico, setContabReceberServico] = useState(false);
    const [contabContasPagar, setContabContasPagar] = useState('');
    const [substituicaoTributaria, setSubstituicaoTributaria] = useState('');
    const [situacaoTributariaIcmsPadrao, setSituacaoTributariaIcmsPadrao] = useState('');
    const [classificacaoTributariaRt, setClassificacaoTributariaRt] = useState('');
    const [classificacaoTributariaIs, setClassificacaoTributariaIs] = useState('');
    const [classificacaoTributariaIbsCbs, setClassificacaoTributariaIbsCbs] = useState('');
    const [mensagemNotaFiscal, setMensagemNotaFiscal] = useState('');

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
    };

    const filteredCFOP = useMemo(() => {
        const filtered = cfopList.filter(item =>
            item.codigoOperacao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return filtered.sort((a, b) => (a.codigoOperacao || "").localeCompare(b.codigoOperacao || "", undefined, { numeric: true }));
    }, [cfopList, searchTerm]);

    const totalPages = Math.ceil(filteredCFOP.length / ITEMS_PER_PAGE);
    const paginatedCFOP = filteredCFOP.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const handleOpenModal = (item = null) => {
        setEditingItem(item);
        if (item) {
            setCodigoOperacao(item.codigoOperacao || '');
            setDescricao(item.descricao || '');
            setIcmsIpiMovimentacao(item.icmsIpiMovimentacao || '');
            setSituacaoTributariaIpiEntrada(item.situacaoTributariaIpiEntrada || '');
            setSituacaoTributariaIpiSaida(item.situacaoTributariaIpiSaida || '');
            setCodigoEnquadramento(item.codigoEnquadramento || '');
            setSituacaoTributariaCofins(item.situacaoTributariaCofins || '');
            setSituacaoTributariaPis(item.situacaoTributariaPis || '');
            setMetodoCustoMedio(item.metodoCustoMedio || '');
            setMovimentacaoEstoque(item.movimentacaoEstoque || '');
            setEstoqueTerceirosGera(item.estoqueTerceirosGera || false);
            setComissaoMovimentacao(item.comissaoMovimentacao || '');
            setMovimentacaoNf(item.movimentacaoNf || '');
            setContabContasReceber(item.contabContasReceber || '');
            setContabReceberMercadoria(item.contabReceberMercadoria || false);
            setContabReceberServico(item.contabReceberServico || false);
            setContabContasPagar(item.contabContasPagar || '');
            setSubstituicaoTributaria(item.substituicaoTributaria || '');
            setSituacaoTributariaIcmsPadrao(item.situacaoTributariaIcmsPadrao || '');
            setClassificacaoTributariaRt(item.classificacaoTributariaRt || '');
            setClassificacaoTributariaIs(item.classificacaoTributariaIs || '');
            setClassificacaoTributariaIbsCbs(item.classificacaoTributariaIbsCbs || '');
            setMensagemNotaFiscal(item.mensagemNotaFiscal || '');
        } else {
            resetForm();
        }
        setIsModalOpen(true);
    };

    const resetForm = () => {
        setCodigoOperacao('');
        setDescricao('');
        setIcmsIpiMovimentacao('');
        setSituacaoTributariaIpiEntrada('');
        setSituacaoTributariaIpiSaida('');
        setCodigoEnquadramento('');
        setSituacaoTributariaCofins('');
        setSituacaoTributariaPis('');
        setMetodoCustoMedio('');
        setMovimentacaoEstoque('');
        setEstoqueTerceirosGera(false);
        setComissaoMovimentacao('');
        setMovimentacaoNf('');
        setContabContasReceber('');
        setContabReceberMercadoria(false);
        setContabReceberServico(false);
        setContabContasPagar('');
        setSubstituicaoTributaria('');
        setSituacaoTributariaIcmsPadrao('');
        setClassificacaoTributariaRt('');
        setClassificacaoTributariaIs('');
        setClassificacaoTributariaIbsCbs('');
        setMensagemNotaFiscal('');
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        const payload = {
            codigoOperacao,
            descricao,
            icmsIpiMovimentacao,
            situacaoTributariaIpiEntrada,
            situacaoTributariaIpiSaida,
            codigoEnquadramento,
            situacaoTributariaCofins,
            situacaoTributariaPis,
            metodoCustoMedio,
            movimentacaoEstoque,
            estoqueTerceirosGera,
            comissaoMovimentacao,
            movimentacaoNf,
            contabContasReceber,
            contabReceberMercadoria,
            contabReceberServico,
            contabContasPagar,
            substituicaoTributaria,
            situacaoTributariaIcmsPadrao,
            classificacaoTributariaRt,
            classificacaoTributariaIs,
            classificacaoTributariaIbsCbs,
            mensagemNotaFiscal,
            updatedAt: new Date()
        };
        try {
            if (editingItem) {
                await updateDocument(editingItem.id, payload);
                addToast(t('pages.cfop.toasts.updateSuccess'), 'success');
            } else {
                await addDocument({ ...payload, createdAt: new Date() });
                addToast(t('pages.cfop.toasts.createSuccess'), 'success');
            }
            setIsModalOpen(false);
        } catch (err) {
            console.error("Erro ao salvar CFOP:", err);
            addToast(t('pages.cfop.toasts.saveError'), 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('pages.cfop.confirmDelete'))) {
            try {
                await deleteDocument(id);
                addToast(t('pages.cfop.toasts.deleteSuccess'), 'success');
            } catch (err) {
                console.error("Erro ao deletar CFOP:", err);
                addToast(t('pages.cfop.toasts.deleteError'), 'error');
            }
        }
    };

    return (
        <div className={styles.pageContainer}>
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            <PageHeader
                title={t('pages.cfop.title')}
                actionText={can.create ? t('pages.cfop.newCfop') : null}
                onAction={() => handleOpenModal()}
                showAction={can.create}
            />

            <div className={styles.searchBar}>
                <SearchBar
                    placeholder={t('pages.cfop.searchPlaceholder')}
                    onSearch={(term) => {
                        setSearchTerm(term);
                        setCurrentPage(1);
                    }}
                    initialValue={searchTerm}
                />
            </div>

            {loading ? (
                <TableSkeleton columns={3} rows={ITEMS_PER_PAGE} />
            ) : paginatedCFOP.length > 0 ? (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>{t('pages.cfop.table.code')}</th>
                                <th>{t('pages.cfop.table.description')}</th>
                                <th style={{ width: '150px' }}>{t('pages.cfop.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedCFOP.map(item => (
                                <tr key={item.id}>
                                    <td><strong>{item.codigoOperacao}</strong></td>
                                    <td>{item.descricao}</td>
                                    <td>
                                        {can.edit && (
                                            <button onClick={() => handleOpenModal(item)} className={styles.actionButton} title={t('common.edit')}>
                                                <FiEdit />
                                            </button>
                                        )}
                                        {can.delete && (
                                            <button onClick={() => handleDelete(item.id)} className={`${styles.actionButton} ${styles.deleteButton}`} title={t('common.delete')}>
                                                <FiTrash2 />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <EmptyState
                    title={t('pages.cfop.noCfopFound')}
                    message={searchTerm ? t('pages.cfop.noResultsForSearch') : t('pages.cfop.emptyStateMessage')}
                    actionText={can.create ? t('pages.cfop.newCfop') : null}
                    onAction={() => handleOpenModal()}
                />
            )}

            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredCFOP.length}
                    onPageChange={setCurrentPage}
                    itemsLabel={t('common.items')}
                />
            )}

            {/* MODAL CFOP */}
            <FormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleSave}
                title={editingItem ? t('pages.cfop.editCfop') : t('pages.cfop.newCfop')}
                isLoading={formLoading}
                submitText={formLoading ? t('pages.cfop.form.saving') : t('pages.cfop.form.saveCfop')}
                maxWidth="900px"
            >
                {/* BLOCK 1 */}
                <fieldset className={styles.fieldset}>
                    <legend>{t('pages.cfop.form.identification')}</legend>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.code')} *</label>
                            <input type="text" className={styles.input} value={codigoOperacao} onChange={e => setCodigoOperacao(e.target.value)} required />
                        </div>
                        <div className={styles.formGroupFull}>
                            <label>{t('pages.cfop.form.description')} *</label>
                            <input type="text" className={styles.input} value={descricao} onChange={e => setDescricao(e.target.value)} required />
                        </div>
                    </div>
                </fieldset>

                {/* BLOCK 2 */}
                <fieldset className={styles.fieldset}>
                    <legend>{t('pages.cfop.form.icmsIpi')}</legend>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.icmsIpiMovimentacao')}</label>
                            {renderSelectWithPlaceholder(DEBITO_CREDITO_OPTIONS, icmsIpiMovimentacao, e => setIcmsIpiMovimentacao(e.target.value), "-- Selecione --", opt => opt, opt => opt)}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.situacaoTributariaIpiEntrada')}</label>
                            {renderSelectWithPlaceholder(IPI_ENTRADA_OPTIONS, situacaoTributariaIpiEntrada, e => setSituacaoTributariaIpiEntrada(e.target.value), "-- Selecione --")}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.situacaoTributariaIpiSaida')}</label>
                            {renderSelectWithPlaceholder(IPI_SAIDA_OPTIONS, situacaoTributariaIpiSaida, e => setSituacaoTributariaIpiSaida(e.target.value), "-- Selecione --")}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.codigoEnquadramento')}</label>
                            <input type="text" className={styles.input} value={codigoEnquadramento} onChange={e => setCodigoEnquadramento(e.target.value)} />
                        </div>
                    </div>
                </fieldset>

                {/* BLOCK 3 */}
                <fieldset className={styles.fieldset}>
                    <legend>{t('pages.cfop.form.cofinsPis')}</legend>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.situacaoTributariaCofins')}</label>
                            {renderSelectWithPlaceholder(COFINS_OPTIONS, situacaoTributariaCofins, e => setSituacaoTributariaCofins(e.target.value), "-- Selecione --")}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.situacaoTributariaPis')}</label>
                            {renderSelectWithPlaceholder(PIS_OPTIONS, situacaoTributariaPis, e => setSituacaoTributariaPis(e.target.value), "-- Selecione --")}
                        </div>
                    </div>
                </fieldset>

                {/* BLOCK 4 */}
                <fieldset className={styles.fieldset}>
                    <legend>{t('pages.cfop.form.estoqueContabilizacao')}</legend>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.metodoCustoMedio')}</label>
                            {renderSelectWithPlaceholder(METODO_CUSTO_MEDIO_OPTIONS, metodoCustoMedio, e => setMetodoCustoMedio(e.target.value), "-- Selecione --", opt => opt, opt => opt)}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.movimentacaoEstoque')}</label>
                            {renderSelectWithPlaceholder(MOVIMENTACAO_OPTIONS, movimentacaoEstoque, e => setMovimentacaoEstoque(e.target.value), "-- Selecione --", opt => opt, opt => opt)}
                        </div>
                        <div className={styles.formGroup}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" checked={estoqueTerceirosGera} onChange={e => setEstoqueTerceirosGera(e.target.checked)} />
                                {t('pages.cfop.form.estoqueTerceirosGera')}
                            </label>
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.comissaoMovimentacao')}</label>
                            {renderSelectWithPlaceholder(MOVIMENTACAO_OPTIONS, comissaoMovimentacao, e => setComissaoMovimentacao(e.target.value), "-- Selecione --", opt => opt, opt => opt)}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.movimentacaoNf')}</label>
                            {renderSelectWithPlaceholder(MOVIMENTACAO_NF_OPTIONS, movimentacaoNf, e => setMovimentacaoNf(e.target.value), "-- Selecione --", opt => opt, opt => opt)}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.contabContasReceber')}</label>
                            {renderSelectWithPlaceholder(SIM_NAO_OPTIONS, contabContasReceber, e => setContabContasReceber(e.target.value), "-- Selecione --", opt => opt, opt => opt)}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.contabReceberMercadoria')}:</label>
                            <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                                <label><input type="checkbox" checked={contabReceberMercadoria} onChange={e => setContabReceberMercadoria(e.target.checked)} /> {t('pages.cfop.form.contabReceberMercadoria')}</label>
                                <label><input type="checkbox" checked={contabReceberServico} onChange={e => setContabReceberServico(e.target.checked)} /> {t('pages.cfop.form.contabReceberServico')}</label>
                            </div>
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.contabContasPagar')}</label>
                            {renderSelectWithPlaceholder(SIM_NAO_OPTIONS, contabContasPagar, e => setContabContasPagar(e.target.value), "-- Selecione --", opt => opt, opt => opt)}
                        </div>
                    </div>
                </fieldset>

                {/* BLOCK 5 */}
                <fieldset className={styles.fieldset}>
                    <legend>{t('pages.cfop.form.tributacaoComplementos')}</legend>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.substituicaoTributaria')}</label>
                            {renderSelectWithPlaceholder(SUBST_TRIBUTARIA_OPTIONS, substituicaoTributaria, e => setSubstituicaoTributaria(e.target.value), "-- Selecione --", opt => opt, opt => opt)}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.situacaoTributariaIcmsPadrao')}</label>
                            {renderSelectWithPlaceholder(ICMS_ST_OPTIONS, situacaoTributariaIcmsPadrao, e => setSituacaoTributariaIcmsPadrao(e.target.value), "-- Selecione --")}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.classificacaoTributariaRt')}</label>
                            <input type="text" className={styles.input} value={classificacaoTributariaRt} onChange={e => setClassificacaoTributariaRt(e.target.value)} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.classificacaoTributariaIs')}</label>
                            {renderSelectWithPlaceholder(CLASSIF_TRIBUTARIA_IS_OPTIONS, classificacaoTributariaIs, e => setClassificacaoTributariaIs(e.target.value), "-- Selecione --")}
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.cfop.form.classificacaoTributariaIbsCbs')}</label>
                            {renderSelectWithPlaceholder(CLASSIF_TRIBUTARIA_IBS_CBS_OPTIONS, classificacaoTributariaIbsCbs, e => setClassificacaoTributariaIbsCbs(e.target.value), "-- Selecione --")}
                        </div>
                        <div className={styles.formGroupFull}>
                            <label>{t('pages.cfop.form.mensagemNotaFiscal')}</label>
                            <textarea className={styles.textarea} rows={2} value={mensagemNotaFiscal} onChange={e => setMensagemNotaFiscal(e.target.value)} />
                        </div>
                    </div>
                </fieldset>
            </FormModal>
        </div>
    );
};

export default CFOPRegisterPage;